import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { resolveOrganizationBrandName } from '../../utils/branding';
import { isGymSelectableForD28dHost } from '../../utils/organizationView';
import MastersBackHeader from '../common/MastersBackHeader';
import PhoneField from '../ui/PhoneField';
import './d28d-admin-surfaces.css';

const D28D_HOST_ROLE = 'entrenador_d28d';
const GYM_TRAINER_ROLE = 'entrenador_gym';
const D28D_MODULE_ACCESS = { d28d: true, live_classes: true, gym: false, training: false, food_plan: false, nutrition: false };
const GYM_TRAINER_MODULE_ACCESS = { d28d: true, live_classes: true, gym: true, training: false, food_plan: false, nutrition: false };

function hostRoleForPanel(gymScoped) {
  return gymScoped ? GYM_TRAINER_ROLE : D28D_HOST_ROLE;
}

function rolesOf(user) {
  if (!user) return [];
  return Array.isArray(user.roles) && user.roles.length ? user.roles : [user.rol].filter(Boolean);
}

const emptyForm = () => ({
  nombre: '',
  email: '',
  password: '',
  telefono: '',
  gym_id: '',
});

export default function D28dTrainersAdmin({
  onBack = null,
  hasAnyRole = () => false,
  gymScoped = false,
  scopeGymId = null,
  scopeGymName = '',
}) {
  const { user: actor } = useAuth();
  const { t } = useI18n();
  const [hosts, setHosts] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [editingHost, setEditingHost] = useState(null);
  const [editForm, setEditForm] = useState({ nombre: '', email: '', telefono: '', password: '' });
  const [editSaving, setEditSaving] = useState(false);

  const actorGymId = actor?.gym_id || actor?.gymId || null;
  const resolvedGymId = scopeGymId || (gymScoped ? actorGymId : null);
  const isGymPanel = Boolean(gymScoped && resolvedGymId);
  const canPickGym = hasAnyRole(['super_admin', 'admin_d28d']) && !isGymPanel;
  const gymLocked = isGymPanel || (!canPickGym && actorGymId);

  const gymById = useMemo(
    () => Object.fromEntries(gyms.map((g) => [Number(g.id), g])),
    [gyms],
  );

  const selectableGyms = useMemo(
    () => gyms.filter((g) => isGymSelectableForD28dHost(g)),
    [gyms],
  );

  const gymsForHost = useCallback((host) => {
    const gid = Number(host?.gym_id || host?.gymId);
    if (!Number.isFinite(gid) || gid <= 0) return selectableGyms;
    if (selectableGyms.some((g) => Number(g.id) === gid)) return selectableGyms;
    const current = gymById[gid];
    return current ? [...selectableGyms, current] : selectableGyms;
  }, [selectableGyms, gymById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (isGymPanel && resolvedGymId) {
        params.gym_id = resolvedGymId;
      }
      const hostsRes = await api.get('/live-classes/admin/d28d-hosts', { params });
      const list = hostsRes.data?.data || [];
      setHosts(list.sort((a, b) => String(a.nombre).localeCompare(String(b.nombre))));
      if (canPickGym || isGymPanel || gymLocked) {
        const gymsRes = await api.get('/gyms');
        let gymList = Array.isArray(gymsRes.data) ? gymsRes.data : (gymsRes.data?.data || []);
        if (isGymPanel && resolvedGymId) {
          gymList = gymList.filter((g) => Number(g.id) === Number(resolvedGymId));
        }
        setGyms(gymList);
      } else {
        setGyms([]);
      }
    } catch {
      setError(t('d28d.trainers.load_error', 'No se pudieron cargar los entrenadores D28D.'));
      setHosts([]);
    } finally {
      setLoading(false);
    }
  }, [canPickGym, gymLocked, isGymPanel, resolvedGymId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const gid = isGymPanel ? resolvedGymId : (gymLocked ? actorGymId : null);
    if (gid) {
      setForm((f) => ({ ...f, gym_id: String(gid) }));
    }
  }, [gymLocked, isGymPanel, resolvedGymId, actorGymId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return hosts;
    return hosts.filter((h) => {
      const gymName = gymById[Number(h.gym_id || h.gymId)]?.nombre || '';
      return [h.nombre, h.email, gymName].join(' ').toLowerCase().includes(q);
    });
  }, [hosts, search, gymById]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nombre?.trim() || !form.email?.trim()) {
      setError(t('d28d.trainers.name_email_required', 'Nombre y email son obligatorios.'));
      return;
    }
    if (!form.password || form.password.length < 6) {
      setError(t('d28d.trainers.password_required', 'Contraseña mínima de 6 caracteres.'));
      return;
    }
    const gymId = isGymPanel
      ? Number(resolvedGymId)
      : (gymLocked ? actorGymId : (form.gym_id ? parseInt(form.gym_id, 10) : null));
    setSaving(true);
    setError('');
    try {
      await api.post('/admin/users', {
        nombre: form.nombre.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        telefono: form.telefono?.trim() || null,
        rol: hostRoleForPanel(isGymPanel),
        roles: [hostRoleForPanel(isGymPanel)],
        gym_id: gymId,
        gymId: gymId,
        trainer_id: null,
        planId: null,
        module_access: isGymPanel ? GYM_TRAINER_MODULE_ACCESS : D28D_MODULE_ACCESS,
      });
      setForm(emptyForm());
      if (gymLocked || isGymPanel) {
        const gid = isGymPanel ? resolvedGymId : actorGymId;
        setForm({ ...emptyForm(), gym_id: gid ? String(gid) : '' });
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.trainers.save_error', 'No se pudo crear el entrenador D28D.'));
    } finally {
      setSaving(false);
    }
  };

  const assignGym = async (hostId, gymId) => {
    try {
      await api.put(`/admin/users/${hostId}/assign`, {
        gym_id: gymId ? parseInt(gymId, 10) : null,
        trainer_id: null,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.trainers.assign_error', 'No se pudo asignar el gimnasio.'));
    }
  };

  const startEditHost = (host) => {
    setEditingHost(host);
    setEditForm({
      nombre: host.nombre || '',
      email: host.email || '',
      telefono: host.telefono || '',
      password: '',
    });
    setError('');
  };

  const saveEditHost = async (e) => {
    e.preventDefault();
    if (!editingHost?.id) return;
    setEditSaving(true);
    setError('');
    try {
      await api.put(`/admin/users/${editingHost.id}/profile`, {
        nombre: editForm.nombre.trim(),
        email: editForm.email.trim().toLowerCase(),
        telefono: editForm.telefono?.trim() || null,
      });
      if (editForm.password && editForm.password.length >= 6) {
        await api.put(`/admin/users/${editingHost.id}/password`, { password: editForm.password });
      }
      setEditingHost(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.trainers.save_error', 'No se pudo guardar el entrenador.'));
    } finally {
      setEditSaving(false);
    }
  };

  const deleteHost = async (host) => {
    if (!window.confirm(t('d28d.trainers.delete_confirm', '¿Eliminar al entrenador {name}?').replace('{name}', host.nombre || ''))) {
      return;
    }
    try {
      await api.delete(`/admin/users/${host.id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.trainers.delete_error', 'No se pudo eliminar el entrenador.'));
    }
  };

  return (
    <div className="d28d-admin-surface surface-readable dashboard-main-view">
      <header className="dashboard-header panel-admin-header mb-4">
        <div>
          {onBack && isGymPanel ? (
            <MastersBackHeader onBack={onBack} title={t('gym.masters.trainers', 'Entrenadores')} />
          ) : onBack ? (
            <button type="button" className="btn-secondary panel-back-btn mb-2" onClick={onBack}>
              {t('panel.back_services', '← Servicios')}
            </button>
          ) : null}
          {!(onBack && isGymPanel) ? (
            <>
              <h2 className="d28d-page-title">
                {gymScoped
                  ? t('gym.masters.trainers', 'Entrenadores de tu gimnasio')
                  : t('d28d.trainers.title', 'Entrenadores D28D')}
              </h2>
              <p className="d28d-text-muted" style={{ margin: 0 }}>
                {gymScoped
                  ? t(
                    'gym.masters.trainers_desc',
                    'Entrenadores propios de tu sede (rol entrenador de gimnasio): clases en vivo, rutinas y Zoom.',
                  )
                  : t(
                    'd28d.trainers.subtitle',
                    'Hosts operativos del gimnasio virtual: asignar clases en vivo, crear rutinas y anfitrión Zoom. Sin código de vinculación para usuarios finales — separados del módulo Entrenadores del ecosistema.',
                  )}
              </p>
            </>
          ) : (
            <p className="text-sm text-stone-600 mt-1">
              {t(
                'gym.masters.trainers_desc',
                'Entrenadores propios de tu sede (rol entrenador de gimnasio): clases en vivo, rutinas y Zoom.',
              )}
            </p>
          )}
        </div>
      </header>

      {error && (
        <div className="bg-red-50 text-red-800 border border-red-200 p-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      <form onSubmit={handleCreate} className="ui-light-panel d28d-trainers-form border border-slate-200 rounded-xl p-5 mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <label className="ui-field">
          <span className="ui-field__label">{t('common.name', 'Nombre')}</span>
          <input className="ui-input mt-1" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
        </label>
        <label className="ui-field">
          <span className="ui-field__label">{t('common.email', 'Email')}</span>
          <input className="ui-input mt-1" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="ui-field">
          <span className="ui-field__label">{t('common.password', 'Contraseña')}</span>
          <input className="ui-input mt-1" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </label>
        <PhoneField
          label={t('common.phone', 'Teléfono')}
          value={form.telefono}
          onChange={(v) => setForm({ ...form, telefono: v })}
          inputClassName="ui-input mt-1"
          className="ui-field"
          labelClassName="ui-field__label"
        />
        {canPickGym && (
          <label className="ui-field">
            <span className="ui-field__label">{t('common.gym', 'Gimnasio')}</span>
            <select className="ui-input mt-1" value={form.gym_id} onChange={(e) => setForm({ ...form, gym_id: e.target.value })}>
              <option value="">{t('d28d.trainers.platform_host', 'Plataforma D28D (sin sede)')}</option>
              {selectableGyms.map((g) => (
                <option key={g.id} value={g.id}>{g.nombre}</option>
              ))}
            </select>
          </label>
        )}
        {isGymPanel && (
          <label className="ui-field">
            <span className="ui-field__label">{t('common.gym', 'Gimnasio')}</span>
            <p className="ui-input mt-1 bg-stone-50 text-stone-800">
              {scopeGymName || resolveOrganizationBrandName({ entity: gymById[Number(resolvedGymId)] }) || t('gym.masters.your_gym', 'Tu gimnasio virtual')}
            </p>
          </label>
        )}
        <div className="md:col-span-2 lg:col-span-3 flex flex-wrap gap-2 items-center">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? t('common.saving', 'Guardando…') : (gymScoped
              ? t('d28d.trainers.create_gym', '+ Crear entrenador')
              : t('d28d.trainers.create', '+ Crear entrenador D28D'))}
          </button>
          <span className="ui-field__hint">
            {t('d28d.trainers.no_invite_hint', 'No genera código de invitación ni perfil de coach del ecosistema.')}
          </span>
        </div>
      </form>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="font-semibold">
            {t('d28d.trainers.active', 'Entrenadores activos')}
          </h3>
          <input
            type="search"
            className="ui-input max-w-xs"
            placeholder={t('common.search', 'Buscar…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <p className="p-6 text-sm" style={{ color: '#64748b' }}>{t('common.loading', 'Cargando…')}</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm" style={{ color: '#64748b' }}>
            {t('d28d.trainers.none', gymScoped
              ? 'Aún no hay entrenadores. Créalos aquí para asignarlos en clases en vivo.'
              : 'Aún no hay entrenadores D28D. Créalos aquí para asignarlos en clases en vivo.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#475569' }}>{t('common.name', 'Nombre')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#475569' }}>{t('common.email', 'Email')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#475569' }}>{t('common.gym', 'Gimnasio')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#475569' }}>{t('d28d.trainers.role', 'Rol')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase" style={{ color: '#475569' }}>{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium" style={{ color: '#0f172a' }}>{h.nombre}</td>
                    <td className="px-4 py-3" style={{ color: '#334155' }}>{h.email}</td>
                    <td className="px-4 py-3">
                      {canPickGym ? (
                        <select
                          className="ui-input text-sm max-w-[14rem]"
                          value={h.gym_id || h.gymId || ''}
                          onChange={(e) => assignGym(h.id, e.target.value)}
                        >
                          <option value="">{t('d28d.trainers.platform_host', 'Plataforma D28D (sin sede)')}</option>
                          {gymsForHost(h).map((g) => (
                            <option key={g.id} value={g.id}>{g.nombre}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ color: '#334155' }}>
                          {scopeGymName || resolveOrganizationBrandName({ entity: gymById[Number(h.gym_id || h.gymId)] }) || '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="d28d-trainer-role-badge inline-block text-xs font-bold uppercase px-2.5 py-1 rounded-full tracking-wide">
                        {isGymPanel
                          ? t('d28d.trainers.host_badge_gym', 'Entrenador de gimnasio')
                          : t('d28d.trainers.host_badge', 'Host clases + rutinas')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="admin-row-actions admin-row-actions--table justify-end">
                        <button type="button" className="admin-action admin-action--edit" onClick={() => startEditHost(h)} title={t('common.edit', 'Editar')}>
                          <Pencil className="w-4 h-4" aria-hidden />
                          <span>{t('common.edit', 'Editar')}</span>
                        </button>
                        <button type="button" className="admin-action admin-action--danger" onClick={() => deleteHost(h)} title={t('common.delete', 'Eliminar')}>
                          <Trash2 className="w-4 h-4" aria-hidden />
                          <span>{t('common.delete', 'Eliminar')}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingHost ? (
        <div className="form-modal-overlay" role="dialog" aria-modal="true" onClick={() => !editSaving && setEditingHost(null)}>
          <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-stone-900 mb-4">{t('d28d.trainers.edit_title', 'Editar entrenador')}</h3>
            <form onSubmit={saveEditHost} className="space-y-3">
              <label className="ui-field block">
                <span className="ui-field__label">{t('common.name', 'Nombre')}</span>
                <input className="ui-input mt-1 w-full" required value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} />
              </label>
              <label className="ui-field block">
                <span className="ui-field__label">{t('common.email', 'Email')}</span>
                <input className="ui-input mt-1 w-full" type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </label>
              <PhoneField
                label={t('common.phone', 'Teléfono')}
                value={editForm.telefono}
                onChange={(v) => setEditForm({ ...editForm, telefono: v })}
                inputClassName="ui-input mt-1"
                className="ui-field"
                labelClassName="ui-field__label"
              />
              <label className="ui-field block">
                <span className="ui-field__label">{t('users.new_password_optional', 'Nueva contraseña (opcional)')}</span>
                <input className="ui-input mt-1 w-full" type="password" minLength={6} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-secondary" disabled={editSaving} onClick={() => setEditingHost(null)}>{t('common.cancel', 'Cancelar')}</button>
                <button type="submit" className="btn-primary" disabled={editSaving}>{editSaving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
